"use client";

import { useEffect, useRef } from "react";

/**
 * Custom hook to manage session timeout with proper cleanup
 * Tracks timeout handles via useRef to prevent memory leaks
 */
export const useSessionTimer = (
  onExpire: () => void,
  expirationTime: number = 3600 * 1000 // 1 hour default
) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Schedule token expiration
   */
  const scheduleTokenExpiration = () => {
    // Clear existing timer if any
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set new timer
    timerRef.current = setTimeout(() => {
      onExpire();
    }, expirationTime);
  };

  /**
   * Clear the timer
   */
  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  /**
   * Reset the timer (useful for activity-based session management)
   */
  const resetTimer = () => {
    clearTimer();
    scheduleTokenExpiration();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, []);

  return {
    scheduleTokenExpiration,
    clearTimer,
    resetTimer,
  };
};
